import React, { useState, useEffect } from "react";
import { useGetReportQuery, useDeleteReportMutation } from "../../ManagementPortal/services";
import "./styles.scss";
import { useSearchQuery, usePagination, useDelete } from "../../../hooks";
import { useReportWebSocket } from "../../../hooks/UseReportWebSocket";
import { createReportUpdateHandler } from "../../../hooks/UseReportWebSocket/reportUpdateUtils";
import {
  MPSharedHeader,
  MPSharedDeleteModal,
  PaginationHeader,
  PaginationFooter,
} from "../../ManagementPortal/Shared/";
import { QIList } from "@qinvent/react-kit";
import { QIDropdown, QISpinner } from "../../../components";
import { MPDeleteButton, MPViewCsvButton } from "../../ManagementPortal/Shared/Button";
import { capitalize, capitalizeAllFirstLetters } from "../utils";
import { SvgIcon } from "../../Shared";
import { utcToLocalGlobal } from "../../../shared/utility";
import { ReportsForm } from "./Form";
import { FMDPStorage } from "../../../shared/helper";
import sprite from "../../../assets/icons.svg";
import { getLocalizedString } from "../../../shared/translation";
import { getUserIdForFiltering } from "../utils";
import { toast } from "react-toastify";
import { executePDFDownload } from "./pdfDownloadService";
import { prepareCSVDownload, prepareXLSXDownload } from "../../../shared/csvParser";

export const ReportsContainer = () => {
  const { page, setPage, perPage, pagination } = usePagination();

  const { setSimpleSearch, q } = useSearchQuery({
    page,
    setPage,
    simpleSearchKey: "name_cont",
  });

  const currentUser = FMDPStorage.get("current-user");
  const selectedAppId = FMDPStorage.get("selected-app-id");

  const getUserId = () => {
    return getUserIdForFiltering(currentUser, selectedAppId);
  };

  const {
    data = { data: [], total_count: 0 },
    isFetching,
    isSuccess,
    error,
  } = useGetReportQuery({
    page,
    per_page: perPage,
    order_by: "created_at",
    order_dir: "desc",
    q,
    filter_user_id: getUserId(),
  });

  const {
    deleteItem: deleteReport,
    deleteErrorMsg,
    setDeleteErrorMsg,
  } = useDelete({
    deleteMutation: useDeleteReportMutation,
    closeModal: () => setShowDeleteWarning(false),
  });

  const [idToEdit, setIdToEdit] = useState(null);
  const [idToDelete, setIdToDelete] = useState(null);
  const [showForm, setShowForm] = useState(false);
  //const [setIsNew] = useState(false);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [selectedColumnItems, setSelectedColumnItems] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkDeleteWarning, setShowBulkDeleteWarning] = useState(false);
  const [pdfLoading, setPdfLoading] = useState({});
  const [csvLoading, setCsvLoading] = useState({});
  const [xlsxLoading, setXlsxLoading] = useState({});

  // Local state for reports to enable real-time updates
  const [localReports, setLocalReports] = useState(data?.data || []);

  // Update local reports when data changes from API (only when isFetching changes to false)
  useEffect(() => {
    if (!isFetching && data?.data && Array.isArray(data.data)) {
      setLocalReports(data.data);
    }
  }, [isFetching]);

  // WebSocket integration for real-time report status updates
  const handleReportUpdate = async (reportData) => {
    if (!reportData) return;

    // Use functional state update to ensure we work with latest state
    setLocalReports(createReportUpdateHandler(reportData));
  };

  // Initialize WebSocket connection
  const { connectionStatus } = useReportWebSocket(currentUser?.id, handleReportUpdate);

  const createNewHandler = () => {
    setShowForm(true);
    //setIsNew(true);
    setIdToEdit(null);
  };

  const deleteClickHandler = (datum) => {
    setShowDeleteWarning(true);
    setIdToDelete(datum.id);
  };

  const Actions = ({ data }) => {
    const isSelected = selectedIds.includes(data.id);
    return (
      <QIDropdown
        className="more-options-group"
        toggleComponent={
          <SvgIcon
            wrapperClass="more-options"
            name="more-options"
            title={getLocalizedString("more_options", "More Options")}
          />
        }
      >
        <MPDeleteButton
          onClick={() => deleteClickHandler(data)}
          disabled={isSelected}
          style={{ opacity: isSelected ? 0.5 : 1, cursor: isSelected ? "not-allowed" : "pointer" }}
        />
      </QIDropdown>
    );
  };

  const bulkDeleteHandler = () => {
    setShowBulkDeleteWarning(true);
  };

  const handlePDFDownload = (report) => {
    if (pdfLoading[report.id]) return; // Prevent duplicate requests while loading

    setPdfLoading((prev) => ({ ...prev, [report.id]: true }));

    executePDFDownload(report, {
      onLoadingStart: () => {},
      onLoadingEnd: () => {
        setPdfLoading((prev) => ({ ...prev, [report.id]: false }));
      },
      onError: (errorMessage) => {
        if (errorMessage === "Report data is unavailable") {
          toast.error("Report data is unavailable", { autoClose: 5000, closeOnClick: true });
        } else if (errorMessage === "Report contains no data") {
          toast.info("Report contains no data", { autoClose: 5000, closeOnClick: true });
        } else if (errorMessage === "Report data is invalid") {
          toast.error("Report data is invalid", { autoClose: 5000, closeOnClick: true });
        } else if (errorMessage === "PDF generation failed") {
          toast.error("PDF generation failed", { autoClose: 5000, closeOnClick: true });
        } else if (errorMessage === "PDF operation timed out") {
          toast.error("PDF operation timed out", { autoClose: 5000, closeOnClick: true });
        } else {
          toast.error(errorMessage || "PDF generation failed", {
            autoClose: 5000,
            closeOnClick: true,
          });
        }
      },
      onUploadError: (errorMessage) => {
        toast.error("Upload failed", { autoClose: 5000, closeOnClick: true });
      },
    });
  };

  const handleBulkDelete = () => {
    const deletePromises = selectedIds.map((id) =>
      deleteReport({ id })
        .unwrap()
        .catch((error) => ({ id, error })),
    );

    Promise.allSettled(deletePromises).then((results) => {
      const failures = results.filter(
        (result) => result.status === "rejected" || result.value?.error,
      );
      if (failures.length === 0) {
        setSelectedIds([]);
        setShowBulkDeleteWarning(false);
      } else {
        console.error("Some deletions failed:", failures);
        // Keep failed items selected, remove successful ones
        const failedIds = failures.map((f) => f.value?.id).filter(Boolean);
        setSelectedIds(failedIds);
        setShowBulkDeleteWarning(false);
      }
    });
  };

  const headers = [
    {
      label: "",
      key: "checkbox",
      checkbox: true,
      statusKey: "id",
      width: "80px",
    },
    {
      label: getLocalizedString("name", "Name"),
      key: "name",
      className: "name",
    },
    {
      label: getLocalizedString("report_type", "Type"),
      key: "report_type",
      id: 2,
      className: "report-type",
      type: "component",
      component: ({ data }) => {
        //Underscore is replaced by space and first letter of each word is made in upper case
        let name = data?.report_type?.split("_").map((word) => {
          // Display Activity instead of Activities
          return word === "Activities"
            ? "Activity"
            : word.charAt(0).toUpperCase() + word.substring(1, word.length) + " ";
        });

        name = name?.map((s) => s?.trim())?.join(" ");

        return (
          <span
            title={`${name} ${
              data?.report_type?.toLowerCase() === "trackpoint" &&
              data?.report_sub_type?.toLowerCase() === "detailed"
                ? "(" + data?.report_configuration_name + ")"
                : ""
            }`}
          >
            {`${name} ${
              data?.report_type?.toLowerCase() === "trackpoint" &&
              data?.report_sub_type?.toLowerCase() === "detailed"
                ? "(" + data?.report_configuration_name + ")"
                : ""
            }`}
          </span>
        );
      },
    },
    {
      label: getLocalizedString("subtype", "Sub-Type"),
      key: "report_sub_type",
      className: "report-sub-type",
      type: "component",
      component: ({ data }) => {
        const subType = data?.report_sub_type?.split("_").map((word) => {
          return word.charAt(0).toUpperCase() + word.substring(1, word.length) + " ";
        });

        const summaryInterval = data?.summary_interval?.split("_").map((word) => {
          // Display NA instead of na
          return word === "na"
            ? "NA"
            : word.charAt(0).toUpperCase() + word.substring(1, word.length);
        });

        return (
          <span title={`${subType} ${summaryInterval ? "(" + summaryInterval + ")" : ""}`}>
            {`${subType} ${summaryInterval ? "(" + summaryInterval + ")" : ""}`}
          </span>
        );
      },
    },
    {
      label: getLocalizedString("selected_criteria", "Selected Criteria"),
      key: "selection_criteria",
      id: 4,
      className: "selection-criteria",
      type: "component",
      component: ({ data }) => {
        return (
          <span
            title={`${data?.selection_criteria} ${
              data?.asset_type ? "(" + data?.asset_type + ")" : ""
            }`}
          >
            {`${data?.selection_criteria} ${data?.asset_type ? "(" + data?.asset_type + ")" : ""}`}
          </span>
        );
      },
    },
    {
      label: getLocalizedString("group_by_asset", "Group By Asset"),
      key: "group_by_asset",
      nestedValue: true,
      getNestedValue: ({ group_by_asset }) => (
        <>
          {group_by_asset ? (
            <SvgIcon name="tick" wrapperClass="status-icon active" svgClass="=icon-tick" />
          ) : (
            <SvgIcon name="cross" wrapperClass="status-icon inactive" svgClass="=icon-cross" />
          )}
        </>
      ),
    },
    {
      label: getLocalizedString("time", "Time"),
      id: 6,
      nestedValue: true,
      getNestedValue: ({ start_time, end_time, report_type, last_active_at_threshold }) => {
        return (
          <>
            {report_type === "Offline" ? (
              <h1 title={utcToLocalGlobal(last_active_at_threshold)}>
                {utcToLocalGlobal(last_active_at_threshold)}
              </h1>
            ) : (
              <h1 title={utcToLocalGlobal(start_time) + " to " + utcToLocalGlobal(end_time)}>
                {utcToLocalGlobal(start_time)}to {utcToLocalGlobal(end_time)}
              </h1>
            )}
          </>
        );
      },
    },
    {
      label: getLocalizedString("applicable_days", "Applicable Days"),
      key: "applicable_days_type",
      className: "applicable-days",
    },
    {
      label: getLocalizedString("created_by", "Created By"),
      id: 8,
      key: "created_by",
      className: "added-by",
    },
    {
      label: getLocalizedString("created_at", "Created At"),
      key: "created_at",
      id: 9,
      nestedValue: true,
      getNestedValue: ({ created_at }) => {
        return <h1 title={utcToLocalGlobal(created_at)}>{utcToLocalGlobal(created_at)}</h1>;
      },
    },
    {
      label: getLocalizedString("format", "Format"),
      key: "file_format",
      className: "file-format",
      type: "component",
      width: "70px",
      resizable: false,
      component: ({ data }) => {
        const rawFormat = data?.file_format?.toLowerCase() || "csv";
        const isExcel = rawFormat === "excel";
        const badgeLabel = isExcel ? "XLSX" : rawFormat.toUpperCase();
        const badgeModifier =
          rawFormat === "pdf"
            ? "format-badge--pdf"
            : isExcel
              ? "format-badge--xlsx"
              : "format-badge--csv";
        const badgeClass = `format-badge ${badgeModifier}`;
        return <span className={badgeClass}>{badgeLabel}</span>;
      },
    },
    {
      label: getLocalizedString("download", "Output"),
      key: "download",
      id: 10,
      className: "action",
      nestedValue: true,
      resizable: false,
      getNestedValue: ({
        report_file_url,
        status,
        id,
        name,
        report_type,
        created_at,
        file_format,
      }) => {
        let statusIcon;
        let wrapperClass = "status-icon";
        switch (status) {
          case "in_progress":
            statusIcon = "time";
            wrapperClass += " idle";
            break;
          case "pending":
            statusIcon = "time";
            wrapperClass += " idle";
            break;
          case "failure":
            statusIcon = "cross";
            wrapperClass += " inactive failed";
            break;
          case "completed":
            statusIcon = "tick";
            wrapperClass += " active";
            break;
          default:
            break;
        }

        // Fleet performance reports use a dedicated preview page
        const getPreviewUrl = () => {
          if (report_type === "vehicle_performance") {
            return `/tracking/reports/fleet-performance-preview`;
          }
          return `/tracking/reports/print-preview?url=${encodeURIComponent(report_file_url)}&format=${encodeURIComponent(file_format || "csv")}`;
        };

        const handleCSVDownload = () => {
          if (csvLoading[id]) return;

          setCsvLoading((prev) => ({ ...prev, [id]: true }));

          const authToken = sessionStorage.getItem("fmdp-access-token");
          const baseUrl = process.env.REACT_APP_CORE_API_URL;
          const downloadUrl = `${baseUrl}/reports/download?report_file_url=${encodeURIComponent(report_file_url)}`;

          fetch(downloadUrl, {
            method: "GET",
            headers: {
              Authorization: authToken || "",
            },
          })
            .then((response) => {
              if (!response.ok) {
                throw new Error(`Download failed with status ${response.status}`);
              }
              const disposition = response.headers.get("Content-Disposition");
              let filename = `${name || "report"}.csv`;
              if (disposition && disposition.includes("filename=")) {
                const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (match && match[1]) {
                  filename = match[1].replace(/['"]/g, "");
                }
              }
              const contentType = response.headers.get("Content-Type") || "";
              return response
                .arrayBuffer()
                .then((fileData) => prepareCSVDownload(fileData, filename, contentType));
            })
            .then(({ blob, filename }) => {
              const url = URL.createObjectURL(blob);
              const anchor = document.createElement("a");
              anchor.href = url;
              anchor.download = filename;
              document.body.appendChild(anchor);
              anchor.click();
              document.body.removeChild(anchor);
              URL.revokeObjectURL(url);
            })
            .catch((error) => {
              console.error("[CSV Download] Failed:", error);
              toast.error("CSV download failed", { autoClose: 5000, closeOnClick: true });
            })
            .finally(() => {
              setCsvLoading((prev) => ({ ...prev, [id]: false }));
            });
        };

        // The backend delivers report.xlsx wrapped inside an outer ZIP archive.
        // prepareXLSXDownload unwraps that archive and returns the workbook, so
        // the saved .xlsx is the actual spreadsheet and not the container ZIP
        // (which Excel rejects with a content-recovery prompt). A response that
        // is already a raw workbook is passed through verbatim.
        const handleXLSXDownload = () => {
          if (xlsxLoading[id]) return;

          setXlsxLoading((prev) => ({ ...prev, [id]: true }));

          const authToken = sessionStorage.getItem("fmdp-access-token");
          const baseUrl = process.env.REACT_APP_CORE_API_URL;
          const downloadUrl = `${baseUrl}/reports/download?report_file_url=${encodeURIComponent(report_file_url)}`;

          fetch(downloadUrl, {
            method: "GET",
            headers: {
              Authorization: authToken || "",
            },
          })
            .then((response) => {
              if (!response.ok) {
                throw new Error(`Download failed with status ${response.status}`);
              }
              const disposition = response.headers.get("Content-Disposition");
              let filename = `${name || "report"}.xlsx`;
              if (disposition && disposition.includes("filename=")) {
                const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (match && match[1]) {
                  filename = match[1].replace(/['"]/g, "");
                }
              }
              const contentType = response.headers.get("Content-Type") || "";
              return response
                .arrayBuffer()
                .then((fileData) => prepareXLSXDownload(fileData, filename, contentType));
            })
            .then(({ blob, filename }) => {
              const url = URL.createObjectURL(blob);
              const anchor = document.createElement("a");
              anchor.href = url;
              anchor.download = filename;
              document.body.appendChild(anchor);
              anchor.click();
              document.body.removeChild(anchor);
              URL.revokeObjectURL(url);
            })
            .catch((error) => {
              console.error("[XLSX Download] Failed:", error);
              toast.error("XLSX download failed", { autoClose: 5000, closeOnClick: true });
            })
            .finally(() => {
              setXlsxLoading((prev) => ({ ...prev, [id]: false }));
            });
        };

        const handleDownload = () => {
          if (file_format === "pdf") {
            handlePDFDownload({ id, name, report_type, created_at, report_file_url });
          } else if (file_format === "excel") {
            handleXLSXDownload();
          } else {
            handleCSVDownload();
          }
        };

        const isLoading =
          file_format === "pdf"
            ? pdfLoading[id]
            : file_format === "excel"
              ? xlsxLoading[id]
              : csvLoading[id];

        const loadingTitle =
          file_format === "pdf"
            ? "Generating PDF..."
            : file_format === "excel"
              ? "Downloading XLSX..."
              : "Downloading CSV...";

        // Backend stores the Excel format as "excel"; surface it to users as "XLSX".
        const formatLabel = file_format === "excel" ? "XLSX" : file_format?.toUpperCase();

        return (
          <div
            style={{ display: "flex", gap: "10px", justifyContent: "center", alignItems: "center" }}
            onClick={(e) => e.stopPropagation()}
          >
            {report_file_url ? (
              <>
                {isLoading ? (
                  <span className="pdf-loading-spinner" title={loadingTitle}>
                    <QISpinner size="18px" />
                  </span>
                ) : (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload();
                    }}
                    className="download-btn"
                    title={`Download ${formatLabel}`}
                  >
                    <SvgIcon
                      name="download"
                      title={`Download ${formatLabel}`}
                      wrapperClass="icon-download"
                      svgClass="icon"
                    />
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(getPreviewUrl(), "_blank");
                  }}
                  className="print-preview-btn"
                  title="Preview"
                >
                  <SvgIcon
                    name="preview"
                    title="Preview"
                    wrapperClass="icon-view"
                    svgClass="icon"
                  />
                </button>
              </>
            ) : status === "completed" && report_type === "vehicle_performance" ? (
              <>
                <SvgIcon
                  name="tick"
                  title="Completed"
                  wrapperClass="status-icon active"
                  svgClass="=icon-tick"
                />
                <button
                  onClick={() => {
                    window.open(getPreviewUrl(), "_blank");
                  }}
                  className="print-preview-btn"
                  title="Preview"
                >
                  <SvgIcon
                    name="preview"
                    title="Preview"
                    wrapperClass="icon-view"
                    svgClass="icon"
                  />
                </button>
              </>
            ) : status === "failure" ? (
              <SvgIcon
                name="cross"
                title="Failed"
                wrapperClass="status-icon inactive failed"
                svgClass="=icon-cross"
              />
            ) : (
              <SvgIcon
                name={statusIcon}
                title={capitalizeAllFirstLetters(status)}
                wrapperClass={wrapperClass}
                svgClass="=icon-cross"
              />
            )}
          </div>
        );
      },
    },
    {
      label: "Actions",
      key: "actions",
      type: "component",
      component: Actions,
      className: "action",
      resizable: false,
    },
  ].map((header) => ({ ...header, maxWidth: "400px" }));

  const columnData = headers
    .filter((header) => header.key !== "checkbox") // Exclude checkbox from column config
    .map((header, index) => ({
      name: header.label || header.key,
      id: header.key || index,
      enabled: true,
      key: header.key || index,
    }));

  //Updating the selected columns with headers on mount
  useEffect(() => {
    let isMounted = true;

    if (headers && Array.isArray(headers) && isMounted) {
      const filteredHeaders = headers.filter((header) => header.key !== "checkbox");
      setSelectedColumns(filteredHeaders.map((header, idx) => header.key ?? idx));
      setSelectedColumnItems(filteredHeaders.map((header) => header.key));
    }

    return () => {
      isMounted = false;
    };
  }, []);

  //Filtering headers for action column and checkbox
  const filteredHeaders =
    headers.filter(
      (col) =>
        col.className === "action" || col.key === "checkbox" || selectedColumns.includes(col.key),
    ) || [];

  const getDeleteItemName = (id) => {
    let name = "";
    if (localReports && Array.isArray(localReports)) {
      const index = localReports.findIndex((datum) => datum.id === id);
      if (index > -1) {
        name = localReports[index].name;
      }
    }
    return name;
  };

  const reportPagination = { ...pagination, count: data.total_count };

  return (
    <>
      <article className="main-container report-container">
        <MPSharedHeader
          resourceName="Reports"
          createNewHandler={createNewHandler}
          groupName="Reports"
          addNewLabel={getLocalizedString("generate_report", "Generate Report")}
          heading={getLocalizedString("reports", "Reports")}
          handleSearchKey={setSimpleSearch}
          simpleSearchPlaceholder={getLocalizedString("search_by_name", "Search by Name")}
          skipCanCreate={true}
        />
        {selectedIds.length > 0 && (
          <div className="bulk-actions-bar">
            <span className="selected-count">
              {selectedIds.length} {selectedIds.length === 1 ? "item" : "items"} selected
            </span>
            <button className="bulk-delete-btn" onClick={bulkDeleteHandler} title="Delete Selected">
              <SvgIcon name="delete" wrapperClass="icon-delete" />
              Delete Selected
            </button>
          </div>
        )}
        <div className="report-list-content">
          {!isFetching && isSuccess && localReports?.length > 0 ? (
            <QIList
              columns={filteredHeaders}
              data={localReports || []}
              className="report"
              detailedView={false}
              detailedData={localReports || []}
              sortable={false}
              searchable={false}
              showDetails={true}
              columnData={columnData || []}
              columnConfig={true}
              selectedColumns={selectedColumns || []}
              setSelectedColumns={setSelectedColumns}
              scrollbarHeight="100%"
              headers={headers}
              status={selectedIds}
              resizableColumns
              alwaysShowResizeHandles
              handler={{
                allSelectHandler: () => {
                  if (selectedIds.length === localReports.length) {
                    setSelectedIds([]);
                  } else {
                    setSelectedIds(data.data.map((item) => item.id));
                  }
                },
                checkboxHandler: (item) => {
                  if (selectedIds.includes(item.id)) {
                    setSelectedIds(selectedIds.filter((id) => id !== item.id));
                  } else {
                    setSelectedIds([...selectedIds, item.id]);
                  }
                },
                rowHandler: (item) => console.log("Row clicked:", item),
              }}
            />
          ) : (
            !isFetching && (
              <div className="no-data">
                <SvgIcon wrapperClass="no-data-icon" svgClass="icon-noData" name="app-icon" />
                <p>No data available</p>
              </div>
            )
          )}
        </div>
        <aside className="qi-pagination-wrapper">
          <PaginationHeader pagination={reportPagination} />
          {isSuccess && <PaginationFooter pagination={reportPagination} />}
        </aside>
      </article>

      {showForm && <ReportsForm idToEdit={idToEdit} onClose={() => setShowForm(false)} />}

      <MPSharedDeleteModal
        show={showDeleteWarning}
        entityName={getLocalizedString("report", "Report")}
        error={deleteErrorMsg}
        itemName={getDeleteItemName(idToDelete)}
        onHide={() => {
          setDeleteErrorMsg();
          setShowDeleteWarning(false);
        }}
        onDelete={() => deleteReport({ id: idToDelete })}
      />

      <MPSharedDeleteModal
        show={showBulkDeleteWarning}
        entityName={getLocalizedString("reports", "Reports")}
        error={deleteErrorMsg}
        itemName={`${selectedIds.length} selected ${selectedIds.length === 1 ? "report" : "reports"}`}
        onHide={() => {
          setDeleteErrorMsg();
          setShowBulkDeleteWarning(false);
        }}
        onDelete={handleBulkDelete}
      />
    </>
  );
};
